// @flow

import * as React from 'react';

import { useField } from 'react-final-form';
import { useDispatch } from 'react-redux';
import type { Dispatch } from 'redux';
// $FlowFixMe[missing-export]
import { lruMemoize } from 'reselect';

import type { ProductData } from '@gandi/cart';
import { InputTextAnyFieldGroup, debounce } from '@gandi/form/FinalForm';
import { notifyDanger } from '@gandi/notifications';
import { useTranslator } from '@gandi/translate';
import { Base, Block, BlockContent, BlockHeading } from '@gandi/ui';

import { useAppSelector } from 'src/hooks/useAppSelector';

import {
  CERT_ALTNAMES_INPUT_NAME,
  CERT_NUMBER_ADDRESSES_INPUT_NAME,
} from '../../consts';
import {
  mapFormValuesCreateToProductData,
  mapFormValuesRenewToProductData,
} from '../../form/mapper';
import {
  selectCertificateCreatePossibilities,
  selectCertificateRenewPossibilities,
  validateCertificateOptions,
} from '../../modules/certificate';
import type { ValidateCertificateOptionsOptions } from '../../modules/certificate';
import type {
  CertificateApiResponse,
  CertificateProductError,
  FormValues,
} from '../../types';

/**
 * Timeout before starting to validate a manually written field
 */
const DEBOUNCE_TIMEOUT_MANUALLY_TYPED = 800;

/**
 * Memoized csr validator
 *
 * All validators are executed everytime a displayed field is modified unless
 * `validateFields` is provided. But FinalFormArray does not support this
 * feature and always submit the event to all active validators. This memoized
 * function makes sure API isn't called unless csr is modified.
 */
const validateSanMemoized = lruMemoize(
  (
    dispatch: Dispatch<any>,
    productData: ProductData,
    options?: ValidateCertificateOptionsOptions,
  ): Promise<Array<CertificateProductError>> =>
    dispatch(validateCertificateOptions(productData, options)),
);

export type SANOptionProps = {
  isRenew: boolean,
  certToRenew?: CertificateApiResponse,
  isStored?: boolean,
};

type ValidateSan = (
  value: ?Array<string>,
  values: FormValues,
) => Promise<?string>;

export function SANOption({
  isRenew,
  certToRenew,
  isStored,
}: SANOptionProps): React.MixedElement {
  const dispatch = useDispatch();
  const { __ } = useTranslator();
  const {
    input: { value: nbAddresses },
    // $FlowFixMe[prop-missing]
  } = useField(CERT_NUMBER_ADDRESSES_INPUT_NAME, {
    subscription: { value: true },
  });

  const certificateCreatePossibilities = useAppSelector(state =>
    selectCertificateCreatePossibilities(state),
  );
  const certificateRenewPossibilities = useAppSelector(
    selectCertificateRenewPossibilities,
  );

  const validateSan = React.useMemo<ValidateSan>(
    () =>
      debounce<void | string>(async (value, values) => {
        if (
          !value ||
          // In a very specific case and hard to reproduce, «san» value can be
          // `undefined` while the validation happen.
          // The optional chaining on `san` makes sure the there are no error
          // while navigating accross the wizard.
          (Array.isArray(value) && !value.find(san => san?.length > 0))
        ) {
          return;
        }

        if (!isStored && (!values.cn || !values.csr)) {
          return;
        }

        const validateCertificationOptionsOptions = {
          fallbackDCVMethod: 'dns',
        };
        const productData = certToRenew
          ? mapFormValuesRenewToProductData(
              values,
              certToRenew,
              certificateRenewPossibilities,
            )
          : mapFormValuesCreateToProductData(
              values,
              certificateCreatePossibilities,
            );

        try {
          const errors = await validateSanMemoized(
            dispatch,
            productData,
            validateCertificationOptionsOptions,
          );

          if (!errors.length) {
            return;
          }

          // eslint-disable-next-line consistent-return
          return (
            errors
              .filter(err => err.name === 'altnames')
              .map(err => err.description)
              // TODO: Check this display
              .join('<br />')
          );
        } catch (err) {
          // The condition on `formValues.cn` is defensive, goToNextStep should not be called if missing
          if (err.status === 403 && values.cn) {
            dispatch(
              notifyDanger({
                title: __("Certificate can't be validated"),
                content: __("You don't have permission to access %(cn)s", {
                  cn: values.cn,
                }),
              }),
            );
          }
        }
      }, DEBOUNCE_TIMEOUT_MANUALLY_TYPED),
    [
      __,
      certToRenew,
      isStored,
      certificateCreatePossibilities,
      certificateRenewPossibilities,
      dispatch,
    ],
  );

  const help = __(
    'These are the other addresses you want to secure in addition to your Common Name. Note that these alternative names can be changed later.',
  );

  // number of addresses - 1 (because CN is count as an address)
  const numberOfAltNames = Number(nbAddresses) - 1;

  return (
    <Block>
      <BlockHeading>{__('Subject Alternative Name (SAN)')}</BlockHeading>
      <BlockContent>
        <Base marginBottom="small">
          <InputTextAnyFieldGroup
            name={CERT_ALTNAMES_INPUT_NAME}
            maxFields={numberOfAltNames}
            placeholder={__('Enter a SAN here')}
            readOnly={isRenew}
            disabled={!nbAddresses}
            validateFieldArray={validateSan}
          />
        </Base>
        <Base tagName="p" margin="none">
          {help}
        </Base>
      </BlockContent>
    </Block>
  );
}
